
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { SendHorizontal } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface MessageChefProps {
  chefId: string;
  mealId: string;
}

export const MessageChef = ({ chefId, mealId }: MessageChefProps) => {
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Error",
          description: "Please sign in to send messages",
          variant: "destructive",
        });
        return;
      }

      // First, create or get existing conversation
      const { data: conversation, error: conversationError } = await supabase
        .from('conversations')
        .select('id')
        .eq('foodie_id', user.id)
        .eq('cook_id', chefId)
        .eq('meal_id', mealId)
        .maybeSingle();

      let conversationId;

      if (!conversation) {
        const { data: newConversation, error: newConversationError } = await supabase
          .from('conversations')
          .insert({
            foodie_id: user.id,
            cook_id: chefId,
            meal_id: mealId
          })
          .select('id')
          .single();

        if (newConversationError) throw newConversationError;
        conversationId = newConversation.id;
      } else {
        conversationId = conversation.id;
      }

      // Send the message
      const { error: messageError } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversationId,
          sender_id: user.id,
          content: message
        });

      if (messageError) throw messageError;

      toast({
        title: "Message sent",
        description: "Your message has been sent to the chef",
      });

      // Navigate to the conversation
      navigate(`/inbox/${conversationId}`);
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      <h3 className="font-semibold text-black">Message Chef</h3>
      <div className="flex gap-2">
        <Textarea 
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Ask a question about this meal..."
          className="min-h-[100px]"
        />
        <Button 
          size="icon" 
          className="h-10 w-10"
          onClick={handleSendMessage}
          disabled={isLoading || !message.trim()}
        >
          <SendHorizontal className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
